import { combineEpics, ofType } from 'redux-observable';
import { switchMap, catchError, mergeMap, map } from 'rxjs/operators';
import { normalize } from 'normalizr';
import { getTreeFromFlatData, walk } from 'react-sortable-tree';
import api from 'api/index';
import { getActiveSpaceId } from 'spaces/store/selectors';
import { handleError } from 'base/errors/store/actions';
import * as spaceActionTypes from 'spaces/store/actionTypes';
import toast from 'base/components/Toast/toast';
import { history } from 'index';
import * as actionTypes from './actionTypes';
import {
  fetchFolderTree,
  fetchFolderTreeSuccess,
  fetchChildrenSuccess,
  createFolderSuccess,
  updateFolderSuccess,
  deleteFolderSuccess,
  updateMultipleFoldersSuccess,
  fetchChildren,
  fetchFolderDocumentCountBySpaceIdSuccess,
  fetchFolderDocumentCountBySpaceId,
  createMultipleFoldersSuccess,
} from './actions';
import { foldersSchema, folderSchema } from '../../../store/schema';
import { entitiesFetchSuccess } from '../../../store/entities/actions';
import { getActiveFolderId, getFolderTreeBySpaceId } from './selectors';
import { closeAllModals } from '../../../modal';

/**
 * Updates an existing folder tree with new flat data
 * but keeps the expanded state from the previous tree
 * @param prevTree
 * @param newFlatData
 * @returns {*}
 */
function updateFolderTree(prevTree, foldersById, ids) {
  if (!foldersById || foldersById.length === 0) {
    return [];
  }
  // copy expanded state from current tree to new data
  const newFlatData = foldersById;
  walk({
    treeData: prevTree,
    getNodeKey: n => n.id,
    callback: ({ node }) => {
      if (node.expanded && newFlatData[node.id]) {
        newFlatData[node.id].expanded = true;
      }
    },
  });

  // Builds a minimal representation of folders to minimize the tree size
  const buildFolderNode = folder => ({
    id: folder.id,
    name: folder.name,
    parent: folder.parent,
    expanded: folder.expanded,
    route: folder.route,
    canWrite: folder.permissions.canWrite,
    canCreate: folder.permissions.canCreate,
  });

  // convert flat data to tree
  return getTreeFromFlatData({
    // map ids to conserve order
    flatData: ids.map(id => buildFolderNode(newFlatData[id])),
    getKey: node => node.id,
    getParentKey: node => node.parent,
    rootKey: null,
  });
}

const fetchFolderTreeEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.FETCH_FOLDER_TREE),
    mergeMap(action =>
      api.folders.fetchTreeBySpaceId(action.spaceId).pipe(
        switchMap(response => {
          const { entities, result } = normalize(response.body, foldersSchema);
          const prevTree = getFolderTreeBySpaceId(state$.value, action.spaceId);
          const folderTree = updateFolderTree(prevTree, entities.folders, result);

          return [entitiesFetchSuccess(entities), fetchFolderTreeSuccess(result, action.spaceId, folderTree)];
        }),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const fetchChildrenEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.FETCH_CHILDREN),
    switchMap(action =>
      api.folders.fetchChildren(action.spaceId, action.parentId).pipe(
        map(response => normalize(response.body, foldersSchema)),
        mergeMap(({ entities, result }) => [entitiesFetchSuccess(entities), fetchChildrenSuccess(result)]),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const fetchFolderDocumentCountEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.FETCH_FOLDER_DOCUMENT_COUNT),
    switchMap(action =>
      api.folders.fetchFolderDocumentCountBySpaceId(action.spaceId).pipe(
        mergeMap(response => {
          const documentCountByFolderId = response.body.reduce(
            (obj, item) => ({
              ...obj,
              [item.id]: item.count,
            }),
            {},
          );
          return [fetchFolderDocumentCountBySpaceIdSuccess(documentCountByFolderId)];
        }),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const fetchFolderPerSpaceHelper = spaceIds => spaceIds.map(spaceId => fetchFolderTree(spaceId));

const fetchAllFolderTreesEpic = action$ =>
  action$.pipe(
    ofType(spaceActionTypes.FETCH_ALL_FOR_ALL_FOLDER_TREES_SUCCESS),
    mergeMap(action => [...fetchFolderPerSpaceHelper(action.ids)]),
  );

const createFolderEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.CREATE),
    mergeMap(action =>
      api.folders.create(action.folder).pipe(
        mergeMap(response => {
          const { entities, result } = normalize(response.body, folderSchema);
          const spaceId = getActiveSpaceId(state$.value);
          return [entitiesFetchSuccess(entities), createFolderSuccess(result), fetchFolderTree(spaceId)];
        }),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const createMultipleFoldersEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.CREATE_MULTIPLE),
    mergeMap(action =>
      api.folders.createMultiple(action.multipleFolders).pipe(
        mergeMap(response => {
          const { entities, result } = normalize(response.body, foldersSchema);
          const spaceId = getActiveSpaceId(state$.value);
          return [entitiesFetchSuccess(entities), createMultipleFoldersSuccess(result), fetchFolderTree(spaceId)];
        }),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const updateFolderEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.UPDATE),
    mergeMap(action =>
      api.folders.update(action.folder).pipe(
        mergeMap(response => {
          const { entities, result } = normalize(response.body, folderSchema);
          const spaceId = getActiveSpaceId(state$.value);
          const folderId = getActiveFolderId(state$.value);
          const nextActions = [
            entitiesFetchSuccess(entities),
            updateFolderSuccess(result),
            fetchChildren(spaceId, folderId),
          ];
          if (action.fetchFolderTreeAfterUpdate) {
            nextActions.push(fetchFolderTree(spaceId));
          }
          return nextActions;
        }),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const updateMultipleFoldersEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.UPDATE_MULTIPLE),
    mergeMap(action =>
      api.folders.updateMultiple(action.folders).pipe(
        switchMap(response => {
          const { entities, result } = normalize(response.body, foldersSchema);
          const spaceId = getActiveSpaceId(state$.value);
          const nextActions = [entitiesFetchSuccess(entities), updateMultipleFoldersSuccess(result)];
          if (spaceId) {
            nextActions.push(fetchChildren(spaceId));
            nextActions.push(fetchFolderDocumentCountBySpaceId(spaceId));
          }
          return nextActions;
        }),
        catchError(error => {
          if (error.status && error.status === 403) {
            toast.warn('Access denied - could not update all folders');
            const spaceId = getActiveSpaceId(state$.value);
            return [fetchFolderTree(spaceId)];
          }
          return [handleError(error)];
        }),
      ),
    ),
  );

const deleteFolderEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.DELETE),
    mergeMap(action =>
      api.folders.delete(action.folder).pipe(
        mergeMap(() => {
          const spaceId = getActiveSpaceId(state$.value);
          return [deleteFolderSuccess(action.folder.id), fetchFolderTree(spaceId)];
        }),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const gotoFolderEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.NAVIGATE_TO_FOLDER),
    switchMap(action => {
      history.push(action.folder.route);
      return [closeAllModals()];
    }),
  );

export default combineEpics(
  fetchFolderTreeEpic,
  fetchChildrenEpic,
  fetchFolderDocumentCountEpic,
  updateFolderEpic,
  updateMultipleFoldersEpic,
  createFolderEpic,
  createMultipleFoldersEpic,
  deleteFolderEpic,
  gotoFolderEpic,
  fetchAllFolderTreesEpic,
);
